<script setup lang="ts">
  defineOptions({
    name: 'ChatPanel',
  })

  import { computed, ref, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import type { ChatTabValue } from '@communities/chats-layer/types/chat-tabs.types'
  import { useChatParticipant } from '../../composables/useChatParticipant'
  import { useChats } from '../../composables/useChats'

  import { getConversationUnreadCount } from '../../utils/conversation-unread.utils'

  import NewChatFAB from '../atoms/NewChatFAB.vue'

  import ChatPanelHeader from '../molecules/ChatPanelHeader.vue'

  import ChatTabs from '../molecules/ChatTabs.vue'

  import ConversationList from '../molecules/ConversationList.vue'

  import NewChatView from '../molecules/NewChatView.vue'

  import type { IConversation } from '../../types/conversation.types'

  const props = defineProps({
    width: {
      type: [Number, String],
      default: 380,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    selectConversation: [conversation: IConversation]
    close: []
  }>()

  // Composables
  const { t } = useI18n()

  const chats = useChats()
  const { getCurrentUserId } = useChatParticipant()

  // Active tab
  const defaultActiveTab: ChatTabValue = 'all'

  const activeTab = ref<ChatTabValue>(defaultActiveTab)

  // Fetch conversations for both tabs
  const allConversationsQuery = chats.fetchConversations()

  const unreadConversationsQuery = chats.fetchConversations({ unread: true })

  const allConversations = allConversationsQuery.allData

  const unreadConversations = unreadConversationsQuery.allData

  const activeConversationsQuery = computed(() => {
    return activeTab.value === 'unread' ? unreadConversationsQuery : allConversationsQuery
  })

  // Platform chat id (GoKollab contactId, UserV2 userId) for unread ownership
  const currentUserId = computed(() => getCurrentUserId())

  // Filter conversations based on active tab
  const conversations = computed(() => {
    switch (activeTab.value) {
      case 'unread':
        return unreadConversations.value.filter(hasUnreadMessages)
      default:
        return allConversations.value
    }
  })

  // Compute counts for tabs
  const unreadCount = computed(() => {
    return allConversations.value.filter(hasUnreadMessages).length
  })

  // Handle conversation selection - opens a new chat window
  const handleSelectConversation = (conversation: IConversation) => {
    chats.selectConversationAndOpenWindow(conversation)
    emit('selectConversation', conversation)
  }

  // Handle load more (infinite scroll)
  const handleLoadMore = () => {
    const query = activeConversationsQuery.value
    if (query.hasNextPage.value && !query.isFetchingNextPage.value) {
      query.fetchNextPage()
    }
  }

  // Handle panel actions - vertical minimize/expand
  const handleToggleMinimize = () => {
    if (chats.store.isPanelMinimized) {
      chats.store.openPanel()
    } else {
      chats.store.minimizePanel()
    }
  }

  const handleClose = () => {
    chats.store.closePanel()
    emit('close')
  }

  // Handle new chat FAB click
  const handleNewChatClick = () => {
    chats.store.showNewChatView()
  }

  // Handle conversation created from new chat view
  const handleConversationCreated = (conversation: IConversation) => {
    handleSelectConversation(conversation)
  }

  function hasUnreadMessages(conversation: IConversation) {
    return getConversationUnreadCount(conversation, currentUserId.value) > 0
  }

  // Watch for panel state changes
  watch(
    () => chats.store.isPanelOpen,
    isOpen => {
      if (!isOpen) {
        return
      }

      if (allConversationsQuery.isStale.value) {
        allConversationsQuery.refetch()
      }

      if (activeTab.value === 'unread' && unreadConversationsQuery.isStale.value) {
        unreadConversationsQuery.refetch()
      }
    }
  )
</script>

<template>
  <div
    id="chat-panel"
    class="relative flex flex-col overflow-hidden rounded-t-lg border border-b-0 border-default bg-background shadow-lg transition-all duration-300"
    :style="{ width: typeof props.width === 'number' ? `${props.width}px` : props.width }"
    :class="[chats.store.isPanelMinimized ? 'h-auto' : 'h-full']"
    @wheel.stop
    @touchmove.stop
  >
    <!-- Header -->
    <ChatPanelHeader
      v-if="props.showHeader"
      :title="chats.store.isNewChatView ? t('chats.header.startNewChat') : undefined"
      :is-expanded="!chats.store.isPanelMinimized"
      :show-back-button="chats.store.isNewChatView"
      @toggle-expand="handleToggleMinimize"
      @close="handleClose"
      @back="chats.store.showConversationsView()"
    />

    <!-- Content (hidden when minimized) -->
    <template v-if="!chats.store.isPanelMinimized">
      <!-- New Chat View -->
      <NewChatView
        v-if="chats.store.isNewChatView"
        id="new-chat-view"
        class="min-h-0 flex-1"
        @conversation-created="handleConversationCreated"
      />
      <!-- Conversations View -->
      <template v-else>
        <!-- Tabs -->
        <ChatTabs v-model="activeTab" :unread-count="unreadCount" />

        <!-- Conversation List -->
        <ConversationList
          :conversations="conversations"
          :is-loading="activeConversationsQuery.isLoading.value"
          :is-fetching-next-page="activeConversationsQuery.isFetchingNextPage.value"
          :has-next-page="activeConversationsQuery.hasNextPage.value"
          :selected-conversation-id="chats.store.selectedConversationId"
          :current-user-id="currentUserId"
          @select="handleSelectConversation"
          @load-more="handleLoadMore"
          @start-chat="handleNewChatClick"
        />
      </template>
    </template>

    <!-- Floating Action Button - positioned outside content area -->
    <div v-if="!chats.store.isPanelMinimized && !chats.store.isNewChatView" class="absolute bottom-4 right-4">
      <NewChatFAB id="new-chat-fab" @click="handleNewChatClick" />
    </div>
  </div>
</template>
